import React, { useState, useRef, useEffect } from "react";
import "./AudioInterface.css";
import {
  createThread,
  createMessage,
  createRun,
  titleToAssistantIDMap,
} from "../openAIUtils";
import {
  generateTTS as generateTTSCloudFunction,
  transcribeAudio as transcribeAudioCloudFunction,
} from "../openAIUtilsCloudFunctions";
import { titleToImageMap } from "../chatScreen";
import { doc, arrayUnion, updateDoc } from "firebase/firestore";
import { db, auth } from "../../config/firebase-config";
import { useAuthState } from "react-firebase-hooks/auth";

const SILENCE_THRESHOLD = 20;
const SILENCE_DURATION  = 4000;

const AudioInterface = ({
  assistantTitle,
  onClose,
  onAudioSubmit,
  onResponseComplete,
}) => {
  /* ---------- local state ---------- */
  const [isRecording, setRec]    = useState(false);
  const [isPlaying, setPlay]     = useState(false);
  const [responseUrl, setRurl]   = useState(null);
  const [status, setStatus]      = useState("idle"); // idle | recording | …
  const [threadId, setTid]       = useState(null);
  const [user]                   = useAuthState(auth);

  /* ---------- refs ---------- */
  const mediaRecRef   = useRef(null);
  const audioChunks   = useRef([]);
  const lastVolTime   = useRef(Date.now());
  const analyserRef   = useRef(null);
  const audioRef      = useRef(new Audio());
  const playingRef    = useRef(false);
  const accumResp     = useRef("");
  const mounted       = useRef(true);
  const streamRef     = useRef(null);

  const assistantId = titleToAssistantIDMap[assistantTitle];

  /* =========================================================
     1.  INIT / CLEANUP
     ========================================================= */
  useEffect(() => {
    mounted.current = true;
    (async () => {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = s;
      const ctx = new AudioContext();
      const src = ctx.createMediaStreamSource(s);
      const an  = ctx.createAnalyser();
      an.fftSize = 256;
      src.connect(an);
      analyserRef.current = an;
    })().catch(console.error);

    return () => {
      mounted.current = false;
      streamRef.current?.getTracks().forEach(t => t.stop());
      URL.revokeObjectURL(responseUrl || "");
    };
  }, [responseUrl]);

  /* =========================================================
     2.  STOP PLAYBACK
     ========================================================= */
  const stopPlayback = () => {
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    URL.revokeObjectURL(responseUrl || "");
    playingRef.current = false;
    setPlay(false);
    setRurl(null);
    setStatus("idle");
  };

  /* =========================================================
     3.  ASSISTANT STREAM HANDLERS
     ========================================================= */
  const onAssistantToken = (msg) => {
    if (!mounted.current) return;
    if (msg.text === "END_TOKEN") {
      const final = accumResp.current.trim();
      if (final) {
        onAudioSubmit?.(null, final);
        setStatus("generating-audio");
        generateTTS(final);
      }
    } else {
      accumResp.current += msg.text;
    }
  };

  const onStreamError = (err) => {
    console.error(err);
    if (mounted.current) {
      setStatus("idle");
      onResponseComplete?.();
    }
  };

  /* =========================================================
     4.  TTS
     ========================================================= */
  const generateTTS = async (text) => {
    try {
      // Use Cloud Functions instead of direct API call
      const blob = await generateTTSCloudFunction(
        text,
        assistantTitle,
        null,
        {
          model: 'tts-1',
          voice: 'alloy',
        }
      );
      
      if (!mounted.current) return;
      if (playingRef.current) stopPlayback();
      setRurl(URL.createObjectURL(blob));
      setStatus("playing");
    } catch (e) {
      console.error(e);
      setStatus("idle");
    }
  };

  /*  play TTS when ready  */
  useEffect(() => {
    if (!responseUrl) return;
    audioRef.current.src = responseUrl;
    audioRef.current.onended = () => {
      stopPlayback();
      onResponseComplete?.();
    };
    playingRef.current = true;
    audioRef.current.play().catch(console.error);
    setPlay(true);
  }, [responseUrl, onResponseComplete]);

  /* =========================================================
     5.  AUTO‑SILENCE DETECTION
     ========================================================= */
  useEffect(() => {
    if (!isRecording) return;
    const id = setInterval(() => {
      const an = analyserRef.current;
      if (!an) return;
      const arr = new Uint8Array(an.frequencyBinCount);
      an.getByteFrequencyData(arr);
      const avg = arr.reduce((a, b) => a + b) / arr.length;
      if (avg > SILENCE_THRESHOLD) {
        lastVolTime.current = Date.now();
      } else if (Date.now() - lastVolTime.current > SILENCE_DURATION) {
        toggleRecording(); // auto‑send
      }
    }, 400);
    return () => clearInterval(id);
  }, [isRecording]);

  /* =========================================================
     6.  RECORD / STOP
     ========================================================= */
  const toggleRecording = async () => {
    if (playingRef.current) stopPlayback();

    /* ---------- STOP ---------- */
    if (isRecording) {
      mediaRecRef.current?.stop();
      setRec(false);
      setStatus("transcribing");
      return;
    }

    /* ---------- START ---------- */
    try {
      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      const mimeType =
        MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4"
        : "audio/ogg";

      const mr = new MediaRecorder(streamRef.current, { mimeType });
      mediaRecRef.current = mr;
      audioChunks.current  = [];
      accumResp.current    = "";

      mr.ondataavailable = (e) => e.data.size && audioChunks.current.push(e.data);
      mr.onstop = async () => {
        if (!mounted.current) return;

        const blob = new Blob(audioChunks.current, { type: mimeType });
        if (blob.size < 1000) { setStatus("idle"); return; }

        try {
          // Use Cloud Functions instead of direct API call
          // Convert blob to File for the Cloud Function
          const file = new File([blob], `rec.${mimeType.split("/")[1]}`, { type: mimeType });
          const result = await transcribeAudioCloudFunction(file, assistantTitle, null, {
            model: 'whisper-1',
          });
          const spoken = result.text?.trim() || '';
          if (!spoken) { setStatus("idle"); return; }

          onAudioSubmit?.(spoken, null);
          setStatus("processing");

          /* ensure thread */
          let tid = threadId;
          if (!tid) {
            const t = await createThread(assistantTitle);
            tid = t.id;
            setTid(tid);
            if (user) {
              await updateDoc(doc(db, "users", user.uid), {
                [`${assistantTitle}.Threads`]: arrayUnion(tid),
              });
            }
          }

          await createMessage(tid, spoken, assistantTitle);
          createRun(tid, assistantId, onAssistantToken, onStreamError, assistantTitle);
        } catch (e) {
          console.error(e);
          setStatus("idle");
        }
      };

      mr.start();
      setRec(true);
      setStatus("recording");
      lastVolTime.current = Date.now();
    } catch (e) {
      console.error("mic error", e);
    }
  };

  /* =========================================================
     7.  UI
     ========================================================= */
  const statusMsg = ({
    recording:        "Listening… (auto‑send after silence)",
    transcribing:     "Converting speech to text…",
    processing:       "Processing your request…",
    "generating-audio": "Generating audio response…",
    playing:          "Playing response…",
    idle:             "Tap the mic to start speaking.",
  }[status]);

  return (
    <div className="audio-overlay">
      <div className="audio-box">
        <button className="close-btn" onClick={() => { stopPlayback(); onClose(); }}>×</button>
        <h2>Speak to {assistantTitle}</h2>

        <div
          className={`mic-visualizer ${isRecording ? "listening" : ""} ${isPlaying ? "playing" : ""}`}
          onClick={toggleRecording}
        >
          <img src={titleToImageMap[assistantTitle]} alt={`${assistantTitle} logo`} />
        </div>

        <p className="status-message">{statusMsg}</p>
      </div>
    </div>
  );
};

export default AudioInterface;
